import React, { useCallback, useEffect, useState } from 'react';
import { Download, X, AlertCircle } from 'lucide-react';
import { employeeLicenseApi } from '../services/api';
import { isImageCertificate, triggerBlobDownload } from '../utils/certificateFiles';

export type CertificatePreviewModalProps = {
  licenseId: number | null;
  fileName?: string | null;
  title?: string;
  onClose: () => void;
};

const CertificatePreviewModal: React.FC<CertificatePreviewModalProps> = ({
  licenseId,
  fileName,
  title,
  onClose,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const revokePreview = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  useEffect(() => {
    if (!licenseId) {
      revokePreview();
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      revokePreview();
      try {
        const blob = await employeeLicenseApi.downloadCertificate(licenseId, 'inline');
        if (cancelled) return;
        setPreviewUrl(URL.createObjectURL(blob));
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Could not load certificate preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  useEffect(() => {
    if (!licenseId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [licenseId, onClose]);

  useEffect(() => () => revokePreview(), [revokePreview]);

  const handleDownload = async () => {
    if (!licenseId) return;
    try {
      setDownloading(true);
      const blob = await employeeLicenseApi.downloadCertificate(licenseId, 'attachment');
      triggerBlobDownload(blob, fileName || 'certificate');
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  if (!licenseId) return null;

  const displayTitle = title || fileName || 'Certificate';
  const imagePreview = isImageCertificate(fileName);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex flex-col w-full max-w-5xl max-h-[92vh] bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview ${displayTitle}`}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-neutral-900 truncate">{displayTitle}</h2>
            {fileName && fileName !== displayTitle && (
              <p className="text-xs text-neutral-500 truncate">{fileName}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={downloading || loading || Boolean(error)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {downloading ? 'Downloading…' : 'Download'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800"
              aria-label="Close preview"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-[320px] bg-neutral-100 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-80 text-neutral-500">Loading preview…</div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center h-80 px-6 text-center gap-3">
              <AlertCircle className="h-10 w-10 text-red-500" />
              <p className="text-sm text-neutral-700">{error}</p>
            </div>
          )}
          {!loading && !error && previewUrl && imagePreview && (
            <div className="flex items-center justify-center p-4 min-h-[320px]">
              <img src={previewUrl} alt={displayTitle} className="max-w-full max-h-[75vh] object-contain rounded shadow" />
            </div>
          )}
          {!loading && !error && previewUrl && !imagePreview && (
            <iframe
              title={displayTitle}
              src={previewUrl}
              className="w-full h-[75vh] border-0 bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificatePreviewModal;
